import { styled } from '@mui/material';

import {
  Icons,
  Spinner,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import {
  OptimizationWorkloadPodContainer,
  type OptimizationWorkloadPod,
} from '@cast/types';

import {
  getPodAppliedStatus,
  getPodRecommendedRequests,
} from '../../../_podStatusUtils';

const CheckIcon = styled(Icons.CheckCircle)({
  color: tokens.theme.fg.status.success,
});

type Props = {
  isLatestRecommendation: boolean;
  containers: OptimizationWorkloadPodContainer[];
  recommendation?: OptimizationWorkloadPod['recommendation'];
};

export const RecommendationAppliedCell = ({
  isLatestRecommendation,
  containers,
  recommendation,
}: Props) => {
  const recommended = getPodRecommendedRequests(containers);
  const status = getPodAppliedStatus({
    isLatestRecommendation,
    recommendation,
  });

  const renderValues = () => {
    if (!recommended) {
      return (
        <Typography
          variant="P12R"
          component="span"
          sx={{
            color: tokens.theme.fg.structural.secondaryOnInverse,
            fontFamily: tokens.primitives.fontFamily.code,
          }}
        >
          —
        </Typography>
      );
    }
    return (
      <Stack>
        <Box display="flex" gap={4}>
          <Typography
            variant="P12R"
            component="span"
            sx={{
              color: tokens.theme.fg.structural.primaryOnInverse,
              fontFamily: tokens.primitives.fontFamily.code,
            }}
          >
            {recommended.cpuCores.toFixed(3)}
          </Typography>
          <Typography
            component="span"
            variant="P12R"
            sx={{
              color: tokens.theme.fg.structural.secondaryOnInverse,
              fontFamily: tokens.primitives.fontFamily.code,
            }}
          >
            CPU
          </Typography>
        </Box>
        <Box display="flex" gap={4}>
          <Typography
            variant="P12R"
            component="span"
            sx={{
              color: tokens.theme.fg.structural.primaryOnInverse,
              fontFamily: tokens.primitives.fontFamily.code,
            }}
          >
            {recommended.memoryGib.toFixed(3)}
          </Typography>
          <Typography
            component="span"
            variant="P12R"
            sx={{
              color: tokens.theme.fg.structural.secondaryOnInverse,
              fontFamily: tokens.primitives.fontFamily.code,
            }}
          >
            GiB
          </Typography>
        </Box>
      </Stack>
    );
  };

  return (
    <Box display="flex" alignItems="center" gap={12}>
      {status === 'applied' ? (
        <CheckIcon weight="fill" />
      ) : status === 'pending' ? (
        <Spinner
          color="inherit"
          thickness={8}
          sx={{
            color: tokens.theme.fg.status.success,

            '& .MuiCircularProgress-circleDeterminate': {
              stroke: tokens.theme.fg.structural.secondaryOnInverse,
            },
          }}
          size={12}
        />
      ) : (
        // notApplied: no icon, keep the value glyph in the same flex slot so
        // row alignment matches other rows.
        <Box sx={{ width: 12, height: 12 }} />
      )}
      {renderValues()}
    </Box>
  );
};
